// src/App.js
/**
 * Current routing and auth assumptions:
 * - AuthContext manages user state and authentication checks
 * - /api/user endpoint returns user object directly (not nested)
 * - ProtectedRoute checks auth state and shows loading during check
 * - All protected routes wrapped in MainLayout with navigation
 * - Session-based auth via Discord OAuth (cookies)
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AuthProvider } from './contexts/AuthContext';
import HomePage from './Components/HomePage';
import LoginPage from './Components/LoginPage';
import UnauthorizedPage from './Components/UnauthorizedPage';
import ProtectedRoute from './Components/ProtectedRoute';
import MainLayout from './Components/MainLayout';
import BotsPage from './Components/BotsPage';
import BotEditPage from './Components/BotEditPage';
import BotDetailView from './Components/BotDetailView';
import SupportPage from './Components/SupportPage';
import LogsPage from './Components/LogsPage';
import ThreadDetailView from './Components/ThreadDetailView';
import PublicThreadView from './Components/PublicThreadView';
import AdminRoute from './Components/AdminRoute';
import AdminBotsPage from './Components/AdminBotsPage';
import AdminTicketsPage from './Components/AdminTicketsPage';
import AdminSettingsPage from './Components/AdminSettingsPage';
import AccountLinkingPage from './Components/AccountLinkingPage';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#5c6bc0',
      light: '#8e99a4',
      dark: '#3949ab',
    },
    secondary: {
      main: '#f48fb1',
      light: '#f8bbd0',
      dark: '#ec407a',
    },
    background: {
      default: '#0f1318',
      paper: '#171d26',
    },
    success: {
      main: '#66bb6a',
    },
    error: {
      main: '#ef5350',
    },
    warning: {
      main: '#ffa726',
    },
    info: {
      main: '#42a5f5',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    body2: {
      color: 'rgba(255, 255, 255, 0.6)',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            color: 'rgba(255, 255, 255, 0.5)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

function Page({ title, children }) {
  useEffect(() => {
    document.title = title ? `${title} - Highland Networks` : 'Highland Networks';
  }, [title]);
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Page title="Sign In"><LoginPage /></Page>} />
      <Route path="/unauthorized" element={<Page title="Unauthorized"><UnauthorizedPage /></Page>} />
      <Route path="/logs/:threadId/public" element={<Page title="Thread Log"><PublicThreadView /></Page>} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="Dashboard"><HomePage /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bots"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="My Bots"><BotsPage /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bots/:namespace/:botName"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="Edit Bot"><BotEditPage /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bots/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="Edit Bot"><BotEditPage /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bot/:botName/details"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="Bot Details"><BotDetailView /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="Logs"><LogsPage /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logs/:threadId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="Thread Log"><ThreadDetailView /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="Support"><SupportPage /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Page title="Account"><AccountLinkingPage /></Page>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/bots"
        element={
          <AdminRoute>
            <MainLayout>
              <Page title="All Bots"><AdminBotsPage /></Page>
            </MainLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <AdminRoute>
            <MainLayout>
              <Page title="Tickets"><AdminTicketsPage /></Page>
            </MainLayout>
          </AdminRoute>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <AdminRoute>
            <MainLayout>
              <Page title="Settings"><AdminSettingsPage /></Page>
            </MainLayout>
          </AdminRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default App;